import { type ComponentType, type ReactNode, useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { Menu, X, LogOut, ChevronDown, ChevronRight } from 'lucide-react'

export interface MobileWorkstationNavItem {
  to: string
  label: string
  icon: ComponentType<{ className?: string }>
  indent?: boolean
  /**
   * 为 true 时 NavLink 使用 end={false}，子路径（如 /dashboard/resource-calendar）仍视为该项激活。
   * 用于仪表盘子路由等场景。
   */
  matchChildPaths?: boolean
}

export interface MobileWorkstationNavSection {
  title: string
  items: MobileWorkstationNavItem[]
  /** 与平铺菜单行对齐的左侧图标；无则留空位避免标题错位 */
  icon?: ComponentType<{ className?: string }>
}

/** 按顺序混合「平铺链接」与「可折叠分组（单级子菜单）」，用于在侧栏某位置插入可展开父级 */
export type MobileWorkstationNavBlock =
  | { type: 'links'; items: MobileWorkstationNavItem[] }
  | {
      type: 'section'
      title: string
      items: MobileWorkstationNavItem[]
      /** 折叠组标题左侧图标，风格与平铺项一致 */
      icon?: ComponentType<{ className?: string }>
    }

export interface MobileWorkstationLayoutProps {
  title: string
  logoText: string
  logoClassName?: string
  navItems: MobileWorkstationNavItem[]
  /**
   * 若存在且非空，优先于 navItems + navSections 的扁平/分组组合，按块顺序渲染侧栏（支持先平铺、再可折叠、再平铺）。
   * 与 navItems 二选一：传此值时可将 navItems 设为空数组。
   */
  navInOrder?: MobileWorkstationNavBlock[]
  navSections?: MobileWorkstationNavSection[]
  children: ReactNode
  userName?: string
  userAvatar?: string
  onLogout?: () => void
  appName?: string
  contentClassName?: string
  headerExtra?: ReactNode
  sidebarFooter?: ReactNode
  mobilePrimaryNavItems?: MobileWorkstationNavItem[]
  /**
   * 侧栏渲染模式：
   * - sectioned-only: 仅渲染 navSections（默认，避免扁平与分组重复展示）
   * - combined: 同时渲染 navItems + navSections（供需要完整菜单的工作台使用）
   */
  sidebarRenderMode?: 'sectioned-only' | 'combined'
}

function NavigationList({
  items,
  onNavigate,
}: {
  items: MobileWorkstationNavItem[]
  onNavigate?: () => void
}) {
  const location = useLocation()
  const navigate = useNavigate()
  return (
    <nav className="flex flex-col gap-1 px-3 py-0">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={!item.matchChildPaths}
          onClick={(e) => {
            onNavigate?.()
            if (item.to === '/consent' && location.pathname === '/consent' && location.search) {
              e.preventDefault()
              navigate('/consent', { replace: true, state: { resetConsentList: true } })
            }
          }}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              item.indent && 'ml-5 text-[13px]',
              isActive
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/35 dark:text-primary-300'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100',
            )
          }
        >
          <item.icon className="h-5 w-5" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

function SectionedNavigationList({
  sections,
  onNavigate,
}: {
  sections: MobileWorkstationNavSection[]
  onNavigate?: () => void
}) {
  const location = useLocation()
  const navigate = useNavigate()

  // 初始化：当前路由所在的分组默认展开，其余收起
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const defaultOpen = new Set<string>()
    for (const section of sections) {
      const isActive = section.items.some((item) =>
        location.hash ? location.hash.replace('#', '') === item.to : location.pathname === item.to,
      )
      if (isActive) defaultOpen.add(section.title)
    }
    // 若没有匹配（如首次加载），展开第一个分组
    if (defaultOpen.size === 0 && sections.length > 0) defaultOpen.add(sections[0].title)
    return defaultOpen
  })

  const toggleSection = (title: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }

  return (
    <nav className="flex flex-col gap-1 px-3 py-0">
      {sections.map((section) => {
        const isOpen = openSections.has(section.title)
        const hasActiveChild = section.items.some((item) => {
          const hash = location.hash.replace('#', '')
          return hash ? hash === item.to || hash.startsWith(item.to + '/') : false
        })
        const SectionIcon = section.icon

        return (
          <div key={section.title} className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => toggleSection(section.title)}
              className={clsx(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                hasActiveChild
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/35 dark:text-primary-300'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100',
              )}
            >
              {SectionIcon ? (
                <SectionIcon className="h-5 w-5 flex-shrink-0 opacity-90" />
              ) : (
                <span className="h-5 w-5 flex-shrink-0" aria-hidden />
              )}
              <span className="min-w-0 flex-1">{section.title}</span>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-500 opacity-80 dark:text-slate-400" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-500 opacity-80 dark:text-slate-400" />
              )}
            </button>

            {isOpen && (
              <div className="ml-0 flex flex-col gap-1 border-l-2 border-slate-200/80 pl-2.5 dark:border-slate-600/80">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end
                    onClick={(e) => {
                      onNavigate?.()
                      if (item.to === '/consent' && location.pathname === '/consent' && location.search) {
                        e.preventDefault()
                        navigate('/consent', { replace: true, state: { resetConsentList: true } })
                      }
                    }}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                        item.indent && 'ml-1 text-[13px]',
                        isActive
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/35 dark:text-primary-300'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                      )
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

function renderNavInOrder(
  blocks: MobileWorkstationNavBlock[],
  onNavigate?: () => void,
) {
  return (
    <div className="flex flex-col gap-1">
      {blocks.map((block, i) => {
        if (block.type === 'links' && block.items.length > 0) {
          return <NavigationList key={`links-${i}`} items={block.items} onNavigate={onNavigate} />
        }
        if (block.type === 'section' && block.items.length > 0) {
          return (
            <SectionedNavigationList
              key={`sec-${i}`}
              sections={[
                { title: block.title, items: block.items, icon: block.icon },
              ]}
              onNavigate={onNavigate}
            />
          )
        }
        return null
      })}
    </div>
  )
}

function UserIdentity({ userName, userAvatar }: { userName?: string; userAvatar?: string }) {
  if (!userName) return null
  return (
    <div className="flex items-center gap-2">
      {userAvatar ? (
        <img src={userAvatar} alt={userName} className="h-7 w-7 rounded-full" />
      ) : (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700 dark:bg-primary-900/50 dark:text-primary-200">
          {userName.charAt(0)}
        </div>
      )}
      <span className="max-w-[7rem] truncate text-sm text-slate-600 dark:text-slate-200 md:max-w-none">{userName}</span>
    </div>
  )
}

export function MobileWorkstationLayout({
  title,
  logoText,
  logoClassName = 'bg-primary-600',
  navItems,
  navInOrder,
  navSections,
  children,
  userName,
  userAvatar,
  onLogout,
  appName = 'CN KIS',
  contentClassName,
  headerExtra,
  sidebarFooter,
  mobilePrimaryNavItems,
  sidebarRenderMode = 'sectioned-only',
}: MobileWorkstationLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const resolvedInOrder = navInOrder?.filter((b) => {
    if (b.type === 'links') return b.items.length > 0
    if (b.type === 'section') return b.title.trim().length > 0 && b.items.length > 0
    return false
  })
  const resolvedInOrderOrEmpty = resolvedInOrder && resolvedInOrder.length > 0
  const resolvedSections = navSections ?? []
  const hasNav = resolvedInOrderOrEmpty || navItems.length > 0 || resolvedSections.length > 0
  const useInOrderNav = Boolean(resolvedInOrderOrEmpty)
  const useSectionedSidebar = !useInOrderNav && resolvedSections.length > 0 && sidebarRenderMode === 'sectioned-only'
  const useCombinedSidebar = !useInOrderNav && sidebarRenderMode === 'combined'
  const linkItemsForPrimary = useMemo(() => {
    if (useInOrderNav && resolvedInOrder) {
      return resolvedInOrder.flatMap((b) => (b.type === 'links' ? b.items : []))
    }
    return navItems
  }, [useInOrderNav, resolvedInOrder, navItems])
  const primaryNavItems = useMemo(() => {
    if (mobilePrimaryNavItems?.length) return mobilePrimaryNavItems.slice(0, 5)
    return linkItemsForPrimary.slice(0, 5)
  }, [mobilePrimaryNavItems, linkItemsForPrimary])
  const hasBottomNav = primaryNavItems.length > 0
  const headerActions = useMemo(() => {
    return (
      <div className="flex items-center gap-3">
        <div className="hidden sm:block">
          <UserIdentity userName={userName} userAvatar={userAvatar} />
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            className="min-h-11 min-w-11 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            title="退出登录"
          >
            <LogOut className="h-4 w-4" />
          </button>
        )}
      </div>
    )
  }, [onLogout, userAvatar, userName])

  const sidebarNav = useInOrderNav && resolvedInOrder ? (
    renderNavInOrder(resolvedInOrder)
  ) : useCombinedSidebar ? (
    <>
      {navItems.length > 0 ? <NavigationList items={navItems} /> : null}
      {resolvedSections.length > 0 ? <SectionedNavigationList sections={resolvedSections} /> : null}
    </>
  ) : useSectionedSidebar ? (
    <SectionedNavigationList sections={resolvedSections} />
  ) : navItems.length > 0 ? (
    <NavigationList items={navItems} />
  ) : null

  const mobileSidebarNav = useInOrderNav && resolvedInOrder ? (
    renderNavInOrder(resolvedInOrder, () => setMobileMenuOpen(false))
  ) : useCombinedSidebar ? (
    <>
      {navItems.length > 0 ? <NavigationList items={navItems} onNavigate={() => setMobileMenuOpen(false)} /> : null}
      {resolvedSections.length > 0 ? (
        <SectionedNavigationList sections={resolvedSections} onNavigate={() => setMobileMenuOpen(false)} />
      ) : null}
    </>
  ) : useSectionedSidebar ? (
    <SectionedNavigationList sections={resolvedSections} onNavigate={() => setMobileMenuOpen(false)} />
  ) : navItems.length > 0 ? (
    <NavigationList items={navItems} onNavigate={() => setMobileMenuOpen(false)} />
  ) : null

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] overflow-hidden bg-slate-50 dark:bg-slate-900 md:h-screen">
      <aside className="hidden h-full min-h-0 w-56 flex-shrink-0 flex-col border-r border-slate-200 bg-white dark:border-[#3b434e] dark:bg-slate-800 md:flex">
        <div className="flex h-14 shrink-0 items-center border-b border-slate-200 px-5 dark:border-[#3b434e]">
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                'flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white',
                logoClassName,
              )}
            >
              {logoText}
            </div>
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{appName}</span>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto py-3">
          {sidebarNav}
        </div>
        {sidebarFooter ? (
          <div className="shrink-0 border-t border-slate-100 dark:border-[#3b434e]">{sidebarFooter}</div>
        ) : null}
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="relative z-40 flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-3 pt-[env(safe-area-inset-top)] dark:border-slate-700/80 dark:bg-slate-800 md:px-6 md:pt-0">
          <div className="flex items-center gap-2">
            {hasNav && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="min-h-11 min-w-11 rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 md:hidden"
                aria-label="打开导航菜单"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            <h1 className="text-sm font-semibold text-slate-800 dark:text-slate-100 md:text-base">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {headerExtra}
            {headerActions}
          </div>
        </header>

        <main
          className={clsx(
            'flex flex-1 flex-col min-h-0 overflow-hidden',
            contentClassName,
          )}
        >
          <div className="flex-1 min-h-0 overflow-auto p-3 pb-[calc(0.75rem+56px+env(safe-area-inset-bottom))] md:p-6 md:pb-6">
            {children}
          </div>
        </main>
      </div>

      {hasBottomNav && (
        <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur dark:border-[#3b434e] dark:bg-slate-800/95 md:hidden">
          <ul className="grid h-14 grid-cols-5 gap-1">
            {primaryNavItems.map((item) => (
              <li key={item.to} className="min-w-0">
                <NavLink
                  to={item.to}
                  end={!item.matchChildPaths}
                  onClick={(e) => {
                    if (item.to === '/consent' && location.pathname === '/consent' && location.search) {
                      e.preventDefault()
                      navigate('/consent', { replace: true, state: { resetConsentList: true } })
                    }
                  }}
                  className={({ isActive }) =>
                    clsx(
                      'flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-[11px] font-medium',
                      isActive
                        ? 'text-primary-700 dark:text-primary-400'
                        : 'text-slate-500 dark:text-slate-400',
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {mobileMenuOpen && hasNav && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <button
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="关闭导航菜单遮罩"
          />
          <aside className="relative flex h-full w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-xl dark:border-[#3b434e] dark:bg-slate-800">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 px-4 pt-[env(safe-area-inset-top)] dark:border-[#3b434e]">
              <div className="flex items-center gap-2">
                <div
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white',
                    logoClassName,
                  )}
                >
                  {logoText}
                </div>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{appName}</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="min-h-11 min-w-11 rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                aria-label="关闭导航菜单"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="shrink-0 border-b border-slate-100 px-4 py-3 dark:border-[#3b434e]">
              <UserIdentity userName={userName} userAvatar={userAvatar} />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto py-3">
              {mobileSidebarNav}
            </div>
            <div className="shrink-0 pb-[env(safe-area-inset-bottom)]" />
          </aside>
        </div>
      )}
    </div>
  )
}
